// Co-driver calling logic (RALLY_GOAL.md §8): decides *when* and *what* to say. Pure logic —
// voice playback and HUD symbols subscribe to the emitted calls. Deterministic, testable headless.
import { linkWords, type PaceNote, type StageNotes } from './notes';

export type CallTiming = 'early' | 'normal' | 'late';

/** Seconds between the end of a call and the turn-in (minimum guaranteed + preference). */
const TIMING_LEAD: Record<CallTiming, number> = { early: 2.2, normal: 1.5, late: 1.15 };
/** Max assumed acceleration while a call is in flight (m/s², traction limit). */
const ACCEL_ALLOWANCE = 7.5;

export interface Call {
  /** Notes covered by this utterance (chained notes are spoken together). */
  notes: PaceNote[];
  tokens: string[];
  text: string;
  /** Sim time when the call starts and its expected duration (s). */
  t: number;
  duration: number;
  /** Car position (m along the stage) when the call started. */
  s: number;
  compressed: boolean;
}

export interface CallerOptions {
  timing: CallTiming;
  /** Spoken duration of a token sequence (from the voice sprite, or an estimate); compressed calls are read faster. */
  durationOf: (tokens: string[], compressed?: boolean) => number;
}

/** Fallback duration estimate: co-driver cadence ≈ 3.2 syllables/s plus small gaps. */
export function estimateDuration(tokens: string[]): number {
  let syll = 0;
  for (const t of tokens) {
    for (const w of t.split(' ')) syll += Math.max(1, (w.match(/[aeiouy]+/gi) ?? []).length);
  }
  return 0.15 + syll / 3.6 + tokens.length * 0.03;
}

export class Caller {
  readonly notes: PaceNote[];
  private next = 0;
  /** Sim time when the call in progress ends. */
  busyUntil = 0;
  opts: CallerOptions;
  readonly log: Call[] = [];
  onCall: ((c: Call) => void) | null = null;
  private stageCallsDone = new Set<number>();
  private readonly stageNotes: PaceNote[];

  constructor(stage: StageNotes, opts: CallerOptions) {
    this.notes = stage.notes.filter((n) => n.kind !== 'stage');
    this.stageNotes = stage.notes.filter((n) => n.kind === 'stage');
    this.opts = opts;
  }

  /** Re-sync after a reset or an off: skip notes the car has already passed. */
  resync(s: number, now: number): void {
    this.next = this.notes.findIndex((n) => n.s > s + 8);
    if (this.next < 0) this.next = this.notes.length;
    this.busyUntil = now;
    for (const st of this.stageNotes) if (st.s < s) this.stageCallsDone.add(st.id);
  }

  /** Before the start: read the opening notes (up to ~250 m) as real crews do. */
  preStart(startS: number, now: number): Call | null {
    const first: PaceNote[] = [];
    for (let i = this.next; i < this.notes.length && this.notes[i].s < startS + 260 && first.length < 3; i++) first.push(this.notes[i]);
    if (!first.length) return null;
    const call = this.compose(first, false, now, startS);
    this.next += first.length;
    return call;
  }

  /**
   * Called every frame (or every sim step in tests) with the car's distance along the stage and
   * speed. Emits at most one call at a time; returns it when one starts.
   */
  update(s: number, speed: number, now: number): Call | null {
    const v = Math.max(speed, 3);
    // Stage calls (splits, flying finish, stop) at their positions.
    for (const st of this.stageNotes) {
      if (this.stageCallsDone.has(st.id)) continue;
      if (s >= st.s && s < st.s + 60 && now >= this.busyUntil) {
        this.stageCallsDone.add(st.id);
        const c: Call = { notes: [st], tokens: st.tokens, text: st.text, t: now, duration: this.opts.durationOf(st.tokens), s, compressed: false };
        return this.emit(c);
      }
    }
    // Never call a note the car has already reached.
    while (this.next < this.notes.length && this.notes[this.next].s <= s + v * 0.6) this.next++;
    if (this.next >= this.notes.length || now < this.busyUntil) return null;
    // Time to cover a distance allowing for acceleration while calls are in flight (capped gain).
    // Rally2 acceleration is traction-limited (~0.75 g) at low speed and power-limited (~P/m·v) above.
    const acc = Math.min(ACCEL_ALLOWANCE, 120 / Math.max(v, 5));
    const vCap = v + 14;
    const tAcc = (vCap - v) / acc;
    const dAcc = v * tAcc + 0.5 * acc * tAcc * tAcc;
    const timeTo = (d: number) => (d <= 0 ? 0 : d <= dAcc ? (-v + Math.sqrt(v * v + 2 * acc * d)) / acc : tAcc + (d - dAcc) / vCap);
    // The next three utterances; notes linked by "into"/"and" (or < 1.2 s apart) are spoken together.
    const groups: PaceNote[][] = [];
    for (let i = this.next; i < this.notes.length && groups.length < 3; ) {
      const g: PaceNote[] = [this.notes[i]];
      i++;
      while (i < this.notes.length && g.length < 3) {
        const prev = g[g.length - 1];
        if (prev.link === 'into' || prev.link === 'and' || timeTo(this.notes[i].s - s) - timeTo(prev.sEnd - s) < 1.2) g.push(this.notes[i++]);
        else break;
      }
      groups.push(g);
    }
    const plan = (lead: number, compressed: boolean) => {
      const p = groups.map((g) => {
        const dur = this.opts.durationOf(this.tokensFor(g, compressed), compressed);
        return { g, dur, latest: now + timeTo(g[0].s - s) - dur - lead };
      });
      for (let i = p.length - 2; i >= 0; i--) p[i].latest = Math.min(p[i].latest, p[i + 1].latest - p[i].dur - 0.12);
      return p;
    };
    // Preferred timing first; if that no longer fits, the 1.0 s minimum; then compressed calls.
    const pref = TIMING_LEAD[this.opts.timing];
    let compressed = v > 33;
    let p = plan(pref, compressed);
    if (p[0].latest < now) p = plan(1.05, compressed);
    if (p[0].latest < now && !compressed) {
      compressed = true;
      p = plan(1.05, true);
    }
    if (now >= p[0].latest - 0.03) {
      this.next += p[0].g.length;
      return this.compose(p[0].g, compressed, now, s);
    }
    return null;
  }

  private tokensFor(group: PaceNote[], compressed: boolean): string[] {
    const out: string[] = [];
    group.forEach((n, i) => {
      let t = n.tokens;
      if (compressed) {
        // At speed: drop length and line modifiers, keep caution/don't cut/tightens.
        t = t.filter((w) => !['long', 'very long', 'cut', 'keep in', 'keep out', 'plus', 'minus'].includes(w));
      }
      out.push(...t);
      const last = i === group.length - 1;
      if (!last) {
        // Chained: link words between notes ("into"/"and", or the distance if not compressed).
        const l = n.link;
        if (l === 'into' || l === 'and') out.push(l);
        else if (!compressed && l && Number(l) <= 80) out.push(...linkWords(l));
        else out.push('and');
      } else if (!compressed && n.link && n.link !== 'into' && n.link !== 'and') {
        out.push(...linkWords(n.link));
      }
    });
    return out;
  }

  private compose(group: PaceNote[], compressed: boolean, now: number, s: number): Call {
    const tokens = this.tokensFor(group, compressed);
    const c: Call = { notes: group, tokens, text: group.map((n) => n.text).join('  ›  '), t: now, duration: this.opts.durationOf(tokens, compressed), s, compressed };
    return this.emit(c);
  }

  private emit(c: Call): Call {
    this.busyUntil = c.t + c.duration + 0.12;
    this.log.push(c);
    this.onCall?.(c);
    return c;
  }
}
